import { useCallback, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { ColDef, ColGroupDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import capitalize from "lodash/capitalize";

import { ScenarioModelGridContext } from "../../../../types/scenarioModelGridContext";
import {
  useScenarioNamesQuery,
  useScenariosSummaryQuery,
} from "../../../../hooks/useBbe";
import { useScenarioActions } from "../../../../hooks/useScenarioActions";
import { ScenarioSummary } from "../../types";
import { formatNumberValue } from "../../utils";
import { formatDate } from "../../../../utils";
import {
  CARRIER_NAME_MAP,
  SCENARIO_MODEL_MODE,
  ScenarioModelModeType,
} from "../../constants";
import { ScenarioModelModal } from "../ScenarioModelModal";

import { ScenarioModelSideBar } from "./SideBar";
import { ScenarioInnerGroupHeader } from "./ScenarioGroupHeader";

const FIELDS = [
  { key: "bobBonus", label: "BOB Bonus", isTotal: true },
  { key: "carrierName", label: "Carrier" },
  { key: "carrierData", label: "Carrier Data" },
  { key: "carrierBonus", label: "Carrier Bonus" },
  { key: "nbBonus", label: "NB Bonus" },
  { key: "nbPremium", label: "NB Premium" },
  { key: "nbLocCredits", label: "NB LOC Credits" },
  { key: "nbBonusLevel", label: "NB Bonus Level" },
  { key: "persistencyBonus", label: "Persistency Bonus" },
  { key: "persistency", label: "Persistency" },
  { key: "persistencyBonusLevel", label: "Persistency Bonus Level" },
] as const;

type FieldKey = (typeof FIELDS)[number]["key"];

interface FieldRow {
  field: string;
  fieldKey: FieldKey;
  [scenarioCarrierKey: string]: string | number | null | boolean | undefined;
}

function buildRowData(scenarios: ScenarioSummary[]): FieldRow[] {
  return FIELDS.map(({ key, label }) => {
    const row: FieldRow = { field: label, fieldKey: key };

    scenarios.forEach((scenario, sIdx) => {
      scenario.carriers.forEach((carrier) => {
        const colKey = `s${sIdx}_${carrier.carrierId}`;
        row[colKey] = key === "bobBonus" ? scenario.bobBonus : carrier[key];
      });
    });

    return row;
  });
}

function buildColumnDefs(
  scenarios: ScenarioSummary[],
  modelId: string,
): (ColDef<FieldRow> | ColGroupDef<FieldRow>)[] {
  const labelCol: ColDef<FieldRow> = {
    field: "field",
    headerName: "",
    pinned: "left",
    sortable: false,
    suppressMovable: true,
    cellStyle: { fontWeight: 700 },
    suppressHeaderFilterButton: true,
    suppressHeaderMenuButton: true,
  };

  const scenarioGroups: ColGroupDef<FieldRow>[] = scenarios.map(
    (scenario, sIdx) => {
      const isBaseline = scenario.isBaseline;
      return {
        headerName: scenario.name,
        headerClass: "scenario-group-divider",
        headerGroupComponentParams: {
          innerHeaderGroupComponent: ScenarioInnerGroupHeader,
          scenarioId: scenario.id,
          isBaseline,
          modelId,
        },
        children: scenario.carriers.map((carrier) => ({
          field: `s${sIdx}_${carrier.carrierId}`,
          ...(isBaseline && { pinned: "left" as const }),
          headerName: carrier.carrierName as string,
          sortable: false,
          minWidth: 180,
          cellStyle: (params) => {
            const fieldKey = params.data?.fieldKey;
            if (fieldKey === "carrierName") return { textAlign: "left" };
            if (fieldKey === "bobBonus")
              return { fontWeight: 600, textAlign: "right" };
            return { textAlign: "right" };
          },
          colSpan: (params) => {
            if (params.data?.fieldKey === "bobBonus") {
              if (carrier.carrierId === scenario.carriers[0].carrierId)
                return scenario.carriers.length;
              return 1;
            }
            return 1;
          },
          valueFormatter: (params) => {
            if (
              params.data?.fieldKey === "bobBonus" &&
              carrier.carrierId !== scenario.carriers[0].carrierId
            )
              return "";

            if (params.data?.fieldKey === "carrierName") {
              const mapped =
                CARRIER_NAME_MAP[params.value as keyof typeof CARRIER_NAME_MAP];
              return capitalize(mapped ?? params.value) as string;
            }

            if (params.data?.fieldKey === "carrierData") {
              if (!params.value) return "-";
              return formatDate(params.value, true) ?? "-";
            }

            return formatNumberValue(params.data.fieldKey, params.value);
          },
        })),
      };
    },
  );

  return [labelCol, ...scenarioGroups];
}

type ScenarioModelsTableProps = {
  modelId: string;
};

export const ScenarioModelsTable: React.FC<ScenarioModelsTableProps> = ({
  modelId,
}) => {
  const [scenarioModalMode, setScenarioModalMode] =
    useState<ScenarioModelModeType | null>(null);
  const [modalScenarioId, setModalScenarioId] = useState<string>("");
  const [modalScenarioName, setModalScenarioName] = useState<string>("");

  const { data, isLoading, isRefetching } = useScenariosSummaryQuery(modelId);
  const scenarioNames = useScenarioNamesQuery(modelId);

  const scenarios = useMemo(() => data?.scenarios, [data?.scenarios]);

  const rowData = useMemo(() => {
    if (!scenarios?.length) return [];
    return buildRowData(scenarios);
  }, [scenarios]);
  const columnDefs = useMemo(() => {
    if (!scenarios?.length) return [];
    return buildColumnDefs(scenarios, modelId);
  }, [modelId, scenarios]);

  const skeletonRowData: FieldRow[] = useMemo(
    () => FIELDS.map(({ key, label }) => ({ field: label, fieldKey: key })),
    [],
  );

  const skeletonColumnDefs: (ColDef<FieldRow> | ColGroupDef<FieldRow>)[] =
    useMemo(
      () => [
        {
          field: "field",
          headerName: "",
          pinned: "left" as const,
          sortable: false,
          suppressMovable: true,
          cellStyle: { fontWeight: 700 },
          suppressHeaderFilterButton: true,
          suppressHeaderMenuButton: true,
        },
        {
          headerName: "",
          children: [
            {
              field: "col1",
              headerName: "",
              flex: 1,
              sortable: false,
              cellRenderer: () => null,
              cellStyle: { border: "none" },
            },
          ],
        },
      ],
      [],
    );

  const handleCloseModal = (): void => {
    setScenarioModalMode(null);
  };

  const modalIsBaseline =
    scenarios?.find((s) => s.id === modalScenarioId)?.isBaseline ?? false;

  const {
    removeScenario,
    duplicateScenario,
    renameScenario,
    isRenamePending,
    addScenario,
  } = useScenarioActions(
    modalScenarioId,
    modelId,
    handleCloseModal,
    modalIsBaseline,
  );

  const handleScenarioAction = useCallback(
    (
      modalType: ScenarioModelModeType,
      scenarioId: string,
      scenarioName?: string,
    ): void => {
      setScenarioModalMode(modalType);
      setModalScenarioId(scenarioId);

      if (modalType === SCENARIO_MODEL_MODE.Add) {
        const names = scenarioNames?.data ?? [];
        const takenNames = new Set(
          names.map((name) => name.trim().toLowerCase()),
        );
        const nextLetter =
          "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
            .split("")
            .find(
              (letter) => !takenNames.has(`scenario ${letter.toLowerCase()}`),
            ) ?? `${names.length + 1}`;
        setModalScenarioName(`Scenario ${nextLetter}`);
      } else {
        setModalScenarioName(scenarioName ?? "");
      }
    },
    [scenarioNames?.data],
  );

  const onClickConfirm = (input: string): void => {
    if (!scenarioModalMode) return;

    switch (scenarioModalMode) {
      case SCENARIO_MODEL_MODE.Remove:
        removeScenario.mutate();
        break;
      case SCENARIO_MODEL_MODE.Duplicate:
        duplicateScenario.mutate({ name: input });
        break;
      case SCENARIO_MODEL_MODE.RenameScenarioName:
        renameScenario(input);
        break;
      case SCENARIO_MODEL_MODE.Add:
        addScenario.mutate({ name: input });
        break;
    }
  };

  const gridContext: ScenarioModelGridContext = useMemo(
    () => ({ onScenarioAction: handleScenarioAction }),
    [handleScenarioAction],
  );

  const icons = {
    // AG Grid React expects custom icons to be provided as raw HTML/SVG strings,
    // not React components. To match the requested icon style, the iimported MUI
    // `RulesOutlined` icon was convereted into SVG template.
    rules: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.54 11 13 7.46l1.41-1.41 2.12 2.12 4.24-4.24 1.41 1.41zM11 7H2v2h9zm10 6.41L19.59 12 17 14.59 14.41 12 13 13.41 15.59 16 13 18.59 14.41 20 17 17.41 19.59 20 21 18.59 18.41 16zM11 15H2v2h9z"/>
    </svg>`,
  };

  const sideBar = {
    position: "right" as const,
    toolPanels: [
      {
        id: "rules",
        labelDefault: "Rules",
        labelKey: "rules",
        iconKey: "rules",
        toolPanel: ScenarioModelSideBar,
        toolPanelParams: { modelId },
      },
    ],
  };

  return (
    <Box
      className="ag-theme-quartz"
      sx={{
        width: "100%",
        "& .ag-cell": {
          borderRight: "1px solid var(--ag-border-color, #dde2eb)",
        },
        "& .ag-header-cell": {
          borderRight: "1px solid var(--ag-border-color, #dde2eb)",
          width: "100%",
        },
        "& .ag-header-group-cell.scenario-group-divider": {
          borderRight: "1px solid var(--ag-border-color, #dde2eb) !important",
        },
        "& .ag-header-cell-comp-wrapper": {
          width: "100%",
        },
        "& .ag-header-group-cell-label.ag-sticky-label": {
          width: "100% !important",
          maxWidth: "none !important",
        },
        "& .ag-header-group-text": {
          flex: "1 1 auto",
        },
      }}
    >
      <AgGridReact
        domLayout="autoHeight"
        rowData={isLoading ? skeletonRowData : rowData}
        columnDefs={isLoading ? skeletonColumnDefs : columnDefs}
        loading={isLoading || isRefetching}
        suppressMovableColumns
        headerHeight={0}
        groupHeaderHeight={48}
        rowHeight={36}
        pagination={false}
        defaultColDef={{ resizable: false }}
        context={gridContext}
        icons={icons}
        sideBar={sideBar}
      />

      {scenarioModalMode && (
        <ScenarioModelModal
          open={true}
          mode={scenarioModalMode}
          onClose={handleCloseModal}
          defaultValueInput={modalScenarioName}
          description={
            scenarioModalMode === SCENARIO_MODEL_MODE.Remove
              ? `Are you sure you want to delete ${modalScenarioName}? This cannot be undone.`
              : scenarioModalMode === SCENARIO_MODEL_MODE.Duplicate
                ? `Add a new scenario with ${modalScenarioName.replace(/\s*\(copy\)$/i, "")} data as the starting point.`
                : undefined
          }
          onConfirm={onClickConfirm}
          loading={
            removeScenario.isPending ||
            duplicateScenario.isPending ||
            isRenamePending ||
            addScenario.isPending
          }
        />
      )}
    </Box>
  );
};
